import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MapPin, X, Plus, Send, Clock, CheckCircle2, Calendar, UserPlus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import ServicePicker from "@/components/ServicePicker";
import { useAuth } from "@/contexts/AuthContext";

export default function Search() {
  const { user } = useAuth();
  const [zips, setZips] = useState([]);
  const [zipInput, setZipInput] = useState("");
  const [services, setServices] = useState([]);
  const [whenType, setWhenType] = useState("asap"); // asap | 24h | scheduled
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [shareConsent, setShareConsent] = useState(false);
  const [termsAgree, setTermsAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Auto-fill contact info for logged-in customers
  useEffect(() => {
    if (user && user !== false && user.role === "customer") {
      setName((n) => n || user.name || "");
      setEmail((e) => e || user.email || "");
      setPhone((p) => p || user.phone || "");
    }
  }, [user]);

  const addZip = (raw) => {
    const z = (raw || zipInput).trim();
    if (!z || zips.includes(z)) { setZipInput(""); return; }
    setZips([...zips, z]);
    setZipInput("");
  };
  const removeZip = (z) => setZips(zips.filter((x) => x !== z));
  const onZipKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addZip(); }
    else if (e.key === "Backspace" && !zipInput && zips.length) removeZip(zips[zips.length - 1]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!zips.length) { toast.error("Step 1: add at least one zip code"); return; }
    if (!services.length) { toast.error("Step 2: pick at least one service"); return; }
    if (whenType === "scheduled" && (!date || !time)) { toast.error("Step 3: pick a date and time"); return; }
    if (!name.trim()) { toast.error("Step 4: enter your name"); return; }
    if (!email.trim() && !phone.trim()) { toast.error("Step 4: provide an email or phone number"); return; }
    if (!shareConsent) { toast.error("Please approve sharing your contact info with collectors"); return; }
    if (!termsAgree) { toast.error("Please agree to the Customer Acknowledgment and Release"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/service-requests", {
        zip_codes: zips, services,
        when_type: whenType,
        scheduled_date: whenType === "scheduled" ? date : null,
        scheduled_time: whenType === "scheduled" ? time : null,
        name, email: email || null, phone: phone || null, notes,
        agree_terms: termsAgree,
      });
      setSuccess(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="container-x py-16 max-w-2xl" data-testid="request-success">
        <div className="card-soft text-center">
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#2D6A4F]"/>
          <h1 className="font-heading text-3xl text-[#1E3F33] mt-5 font-semibold">Thank you for using <span className="text-[#B91C1C]">Screening Services Mobile</span>!</h1>
          <p className="text-[#5C6B64] mt-3">
            Your contact info, services, notes and timing were sent to <span className="font-medium text-[#1E3F33]">{success.matched_collectors}</span> certified collector{success.matched_collectors === 1 ? "" : "s"} within 100 miles of your zip code. You'll hear back from a collector soon.
          </p>
          <div className="text-sm text-[#8A9A86] mt-2">When: {success.when_label}</div>
        </div>

        <div className="mt-6 rounded-xl border border-[#2D6A4F]/25 bg-[#2D6A4F]/5 p-4 text-left" data-testid="track-link-box">
          <p className="text-sm text-[#1E3F33] font-medium">Your private tracking link</p>
          <p className="text-xs text-[#5C6B64] mt-1">
            We've {success.email && success.phone ? "emailed and texted" : success.email ? "emailed" : "texted"} this to you. Use it to confirm a collector contacted you and leave a review.
          </p>
          <Link
            to={`/my-request/${success.track_token}`}
            data-testid="goto-track-link"
            className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#CC5A3A] hover:underline break-all"
          >
            Track my request →
          </Link>
        </div>

        <div className="text-center">
          <button data-testid="request-new-button" onClick={()=>{ setSuccess(null); setServices([]); setNotes(""); setShareConsent(false); setTermsAgree(false); }} className="btn-secondary mt-6">Submit another request</button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x py-12 max-w-4xl" data-testid="request-form-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-3">Request a collector</div>
        <h1 className="font-heading text-4xl text-[#1E3F33] font-semibold">Tell us what you need.</h1>
        <p className="text-[#5C6B64] mt-2">We'll send your request to certified collectors in your zip code. They'll contact you directly.</p>
      </div>

      <form onSubmit={submit} className="card-soft space-y-8">
        {/* Step 1: Zip */}
        <div data-testid="step-zips">
          <h2 className="font-heading text-lg text-[#1E3F33] mb-1">1. Where are you located?</h2>
          <p className="text-xs text-[#8A9A86] mb-3">Add one or more zip codes. Press Enter to add.</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {zips.map((z) => (
              <span key={z} data-testid={`zip-chip-${z}`} className="inline-flex items-center gap-1.5 bg-[#1E3F33] text-white text-sm rounded-full px-3 py-1">
                <MapPin className="w-3 h-3"/> {z}
                <button type="button" data-testid={`zip-remove-${z}`} onClick={()=>removeZip(z)} className="ml-1 hover:text-[#CC5A3A]"><X className="w-3 h-3"/></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="zip-input" className="input-soft flex-1" placeholder="e.g., 78701" value={zipInput} onChange={(e)=>setZipInput(e.target.value)} onKeyDown={onZipKey} inputMode="numeric"/>
            <button type="button" data-testid="zip-add-button" onClick={()=>addZip()} className="rounded-lg bg-[#1E3F33] text-white px-3 py-2 hover:bg-[#142A22]"><Plus className="w-4 h-4"/></button>
          </div>
        </div>

        {/* Step 2: Services */}
        <div data-testid="step-services">
          <h2 className="font-heading text-lg text-[#1E3F33] mb-1">2. Which services do you need?</h2>
          <p className="text-xs text-[#8A9A86] mb-3">Pick one or more. We'll connect you to collectors offering any of them.</p>
          <ServicePicker selected={services} supplies={{}} showSupplies={false} onChange={(svcs)=>setServices(svcs)} />
        </div>

        {/* Step 3: When */}
        <div data-testid="step-when">
          <h2 className="font-heading text-lg text-[#1E3F33] mb-3">3. When do you need it?</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { v: "asap", l: "ASAP / Same-day", icon: Clock },
              { v: "24h", l: "Within 24 hours", icon: Clock },
              { v: "scheduled", l: "Pick a date & time", icon: Calendar },
            ].map((o)=>(
              <button key={o.v} type="button" data-testid={`when-${o.v}`} onClick={()=>setWhenType(o.v)}
                className={`inline-flex items-center gap-2 text-sm rounded-full px-4 py-2 border transition-colors ${whenType===o.v ? "bg-[#1E3F33] text-white border-[#1E3F33]" : "bg-white text-[#1E3F33] border-[#1E3F33]/15 hover:border-[#1E3F33]/40"}`}>
                <o.icon className="w-4 h-4"/> {o.l}
              </button>
            ))}
          </div>
          {whenType === "scheduled" && (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="when-scheduled-fields">
              <div>
                <label className="label-soft">Date</label>
                <input data-testid="when-date" type="date" required className="input-soft" value={date} onChange={(e)=>setDate(e.target.value)} min={new Date().toISOString().split("T")[0]}/>
              </div>
              <div>
                <label className="label-soft">Time</label>
                <input data-testid="when-time" type="time" required className="input-soft" value={time} onChange={(e)=>setTime(e.target.value)}/>
              </div>
            </div>
          )}
        </div>

        {/* Step 4: Contact */}
        <div data-testid="step-contact">
          <h2 className="font-heading text-lg text-[#1E3F33] mb-1">4. Your contact info</h2>
          <p className="text-xs text-[#8A9A86] mb-3">Provide an email or phone (or both) so collectors can reach you.</p>
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-[#2D6A4F]/25 bg-[#2D6A4F]/5 px-3 py-2.5" data-testid="privacy-glance">
            <ShieldCheck className="w-4 h-4 text-[#2D6A4F] mt-0.5 shrink-0" strokeWidth={1.8} />
            <p className="text-xs text-[#1E3F33]"><span className="font-medium">We never sell your info.</span> It only goes to the matched collectors who can serve your area — once it's sent, our involvement ends.</p>
          </div>
          <div className="mb-4 rounded-xl border border-[#CC5A3A]/30 bg-[#CC5A3A]/5 p-4" data-testid="contact-emphasis">
            <p className="text-sm text-[#1E3F33] font-medium">How we'll reach you</p>
            <p className="text-xs text-[#5C6B64] mt-1">
              The email and phone number you provide will be <span className="font-medium text-[#1E3F33]">sent to the matching collector(s)</span> so they can contact you directly about your request — by <span className="font-medium text-[#1E3F33]">email</span> and <span className="font-medium text-[#1E3F33]">text message (SMS)</span>. Standard message &amp; data rates may apply.
            </p>
            <label className="mt-3 flex items-start gap-2.5 cursor-pointer" data-testid="share-consent-row">
              <input
                type="checkbox"
                data-testid="share-consent-checkbox"
                checked={shareConsent}
                onChange={(e)=>setShareConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#2D6A4F]"
              />
              <span className="text-xs text-[#1E3F33]">I approve sharing my contact info with the matching collector(s) so they can reach out to me.</span>
            </label>
            <label className="mt-2.5 flex items-start gap-2.5 cursor-pointer" data-testid="terms-agree-row">
              <input
                type="checkbox"
                data-testid="terms-agree-checkbox"
                checked={termsAgree}
                onChange={(e)=>setTermsAgree(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#2D6A4F]"
              />
              <span className="text-xs text-[#1E3F33]">I have read and agree to the <a href="/legal/customer-acknowledgment" target="_blank" rel="noreferrer" className="text-[#CC5A3A] font-medium hover:underline" data-testid="terms-link">Customer Acknowledgment and Release</a>.</span>
            </label>
          </div>
          {(!user || user === false) && (
            <Link
              to="/register"
              data-testid="save-info-suggestion"
              className="mb-4 flex items-start gap-3 rounded-xl border border-[#CC5A3A]/30 bg-[#CC5A3A]/5 p-3 hover:bg-[#CC5A3A]/10 transition-colors"
            >
              <UserPlus className="w-5 h-5 text-[#CC5A3A] mt-0.5 shrink-0" strokeWidth={1.6}/>
              <div className="text-sm text-[#1E3F33]">
                <span className="font-medium">Create a free account?</span>
                <span className="text-[#5C6B64]"> We'll save your contact info so you only have to pick zip, services and time. <span className="text-[#CC5A3A] font-medium">Sign up →</span></span>
              </div>
            </Link>
          )}
          {user && user !== false && user.role === "customer" && (
            <div className="mb-4 flex items-center gap-2 rounded-xl border border-[#2D6A4F]/25 bg-[#2D6A4F]/5 p-3 text-sm text-[#1E3F33]" data-testid="auto-fill-banner">
              <CheckCircle2 className="w-4 h-4 text-[#2D6A4F]"/> Your contact info is filled in from your account.
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label-soft">Full name</label>
              <input data-testid="contact-name" required className="input-soft" value={name} onChange={(e)=>setName(e.target.value)}/>
            </div>
            <div>
              <label className="label-soft">Email <span className="text-[#CC5A3A] font-normal">(email or phone required)</span></label>
              <input data-testid="contact-email" type="email" className="input-soft" value={email} onChange={(e)=>setEmail(e.target.value)}/>
            </div>
            <div>
              <label className="label-soft">Phone <span className="text-[#CC5A3A] font-normal">(email or phone required)</span></label>
              <input data-testid="contact-phone" type="tel" className="input-soft" value={phone} onChange={(e)=>setPhone(e.target.value)}/>
            </div>
            <div className="sm:col-span-2">
              <label className="label-soft">Notes (optional)</label>
              <textarea data-testid="contact-notes" rows={3} className="input-soft" value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Anything collectors should know"/>
            </div>
          </div>
        </div>

        <button
          type="submit"
          data-testid="submit-request-button"
          disabled={submitting || !shareConsent || !termsAgree || (!email.trim() && !phone.trim())}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-medium text-white bg-[#2D6A4F] hover:bg-[#235740] transition-colors disabled:opacity-60"
        >
          <Send className="w-4 h-4"/> {submitting ? "Sending…" : "Submit request"}
        </button>
      </form>
    </div>
  );
}
